import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Autocomplete,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";

const PLAN_OPTIONS = [
  "E3 I4",
  "E2 I4",
  "E1 I4",
  "E3 I2",
  "E2 I2",
  "E1 I2",
  "E3 I1",
  "E2 I1",
  "E1 I1",
];

export default function SubscriptionDetails({
  carId,
  userId,
  onEditPrice,
}) {
  // week days for schedule selects
  const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const WEEK_OPTIONS = [
    { value: 1, label: "Week 1" },
    { value: 2, label: "Week 2" },
    { value: 3, label: "Week 3" },
    { value: 4, label: "Week 4" },
  ];
  const WASH_TYPES = [
    { value: "waterless", label: "Waterless" },
    { value: "bucket", label: "Bucket" },
    { value: "jet-spray", label: "Jet Spray" },
    { value: "full-wash", label: "Full Wash" },
  ];

  function getPackCount(pack) {
    // returns object { exterior, interior }
    if (!pack) return { exterior: 0, interior: 0 };
    const exMatch = String(pack).match(/E(\d)/i);
    const inMatch = String(pack).match(/I(\d)/i);
    const exterior = exMatch ? Number(exMatch[1]) : 0;
    const interior = inMatch ? Number(inMatch[1]) : 0;
    return { exterior, interior };
  }

  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editPlan, setEditPlan] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editWashType, setEditWashType] = useState("");
  const [editWasher, setEditWasher] = useState(null);
  const [washerOptions, setWasherOptions] = useState([]);
  const [washerLoading, setWasherLoading] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [interiorSchedule, setInteriorSchedule] = useState({ weeklySchedule: [] });

  // change-washer dialog state
  const [changeWasherOpen, setChangeWasherOpen] = useState(false);
  const [selectedChangeWasher, setSelectedChangeWasher] = useState(null);
  const [cwPrice, setCwPrice] = useState("");
  const [cwTxnId, setCwTxnId] = useState("");
  const [changeSubmitting, setChangeSubmitting] = useState(false);

  // pause dialog state
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseDate, setPauseDate] = useState("");
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  // skip action state
  const [skipSubmitting, setSkipSubmitting] = useState(false);
  const handleSkip = async () => {
    if (!subscriptions?._id) {
      alert("No subscription selected");
      return;
    }
    const ok = window.confirm("Are you sure you want to skip the subscription?");
    if (!ok) return;
    setSkipSubmitting(true);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/skip/${subscriptions._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      
      setSkipSubmitting(false);
      if (!res.ok) throw new Error("Failed to skip subscription");
      const updated = await res.json();
      setSubscriptions((prev) => ({ ...(prev || {}), ...(updated || {}) }));
      fetchSubscriptions();
    } catch (err) {
      console.error(err);
      alert("Failed to skip subscription");
    } finally {
      setSkipSubmitting(false);
    }
  };

  const handleScheduleChange = (index, field, value) => {
    setSchedule((s) => {
      const next = Array.isArray(s) ? [...s] : [];
      while (next.length <= index) next.push({ day: "", time: "" });
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleWeeklyInteriorScheduleChange = (index, field, value) => {
    setInteriorSchedule((prev) => {
      const wk = Array.isArray(prev.weeklySchedule) ? [...prev.weeklySchedule] : [];
      while (wk.length <= index) wk.push({ week: 1, day: "" });
      wk[index] = { ...wk[index], [field]: value };
      return { ...prev, weeklySchedule: wk };
    });
  };

  const openChangeWasherDialog = () => {
    // preload current values
    if (subscriptions) {
      const currentWasher = subscriptions.washerDetails || subscriptions.washerId || null;
      setSelectedChangeWasher(currentWasher ? (currentWasher._id ? currentWasher : { _id: currentWasher, name: currentWasher?.name || "" }) : null);
      setCwPrice(subscriptions.currentSubscriptionPlanPrice ?? "");
      // try common txn fields
      setCwTxnId(subscriptions.transactionId ?? subscriptions.currentTxnId ?? subscriptions.txnId ?? "");
    }
    // ensure washer options loaded
    fetchWasherOptions();
    setChangeWasherOpen(true);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const q = new URLSearchParams();
      if (carId) q.set("carId", carId);
      if (userId) q.set("userId", userId);

      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/list/one?${q.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch subscriptions");
      const result = await res.json();
      console.log(result.data);
      setSubscriptions(result ? result : {});
    } catch (err) {
      console.error(err);
      setError(err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carId && !userId) return;
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId, userId]);

  // prepare edit form when entering edit mode
  useEffect(() => {
    if (isEditing && subscriptions) {
      setEditPlan(subscriptions.currentSubscriptionPlan || "");
      setEditWashType(subscriptions.currentWashType || "");
      setEditPrice(subscriptions.currentSubscriptionPlanPrice ?? "");
      // seed washer as object if possible
      if (subscriptions.washerDetails) setEditWasher(subscriptions.washerDetails);
      else if (subscriptions.washerId) setEditWasher({ _id: subscriptions.washerId._id, name: subscriptions.washerId.name });
      else setEditWasher(null);
      // init schedule from subscription or create empty slots based on plan
      const existing = subscriptions.currentSchedule;
      if (Array.isArray(existing) && existing.length > 0) {
        // normalize to array of {day, time}
        setSchedule(existing.map((e) => (typeof e === "object" ? { day: e.day || "", time: e.time || "" } : { day: "", time: String(e || "") })));
        setInteriorSchedule({ weeklySchedule: [] });
      } else if (existing && typeof existing === "object") {
        const ext = Array.isArray(existing.exterior) ? existing.exterior : [];
        setSchedule(ext.map((e) => (typeof e === "object" ? { day: e.day || "", time: e.time || "" } : { day: "", time: String(e || "") })));
        const wk = (existing.interior && Array.isArray(existing.interior.weeklySchedule))
          ? existing.interior.weeklySchedule
          : Array.isArray(existing.interior) ? existing.interior : [];
        setInteriorSchedule({ weeklySchedule: wk.map((it) => ({ week: it.week ?? 1, day: it.day ?? "" })) });
      } else {
        const cnt = getPackCount(subscriptions.currentSubscriptionPlan || "");
        setSchedule(Array.from({ length: cnt.exterior }, () => ({ day: "", time: "" })));
        setInteriorSchedule({ weeklySchedule: Array.from({ length: cnt.interior }, () => ({ week: 1, day: "" })) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, subscriptions]);

  // when editPlan changes, ensure schedule length matches pack count
  useEffect(() => {
    const cnt = getPackCount(editPlan);
    // exterior
    setSchedule((s) => {
      const cur = Array.isArray(s) ? [...s] : [];
      if (cur.length === cnt.exterior) return cur;
      if (cur.length > cnt.exterior) return cur.slice(0, cnt.exterior);
      while (cur.length < cnt.exterior) cur.push({ day: "", time: "" });
      return cur;
    });
    // interior
    setInteriorSchedule((prev) => {
      const cur = Array.isArray(prev.weeklySchedule) ? [...prev.weeklySchedule] : [];
      if (cur.length === cnt.interior) return { weeklySchedule: cur };
      if (cur.length > cnt.interior) return { weeklySchedule: cur.slice(0, cnt.interior) };
      while (cur.length < cnt.interior) cur.push({ week: 1, day: "" });
      return { weeklySchedule: cur };
    });
  }, [editPlan]);

  const fetchWasherOptions = async (search = "") => {
    setWasherLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      const data = await res.json();
      setWasherOptions(Array.isArray(data) ? data : data?.data ?? []);
    } catch (e) {
      setWasherOptions([]);
    } finally {
      setWasherLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!subscriptions?._id) return;
    // build consistent schedule payload: exterior + interior.weeklySchedule
    const body = {
      subscriptionPlan: editPlan,
      currentWashType: editWashType,
      price: Number(editPrice) || 0,
      currentSchedule: {
        exterior: schedule,
        interior: { weeklySchedule: interiorSchedule.weeklySchedule || [] },
      },
    };
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/update/${subscriptions._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error("Update failed");
      const updated = await res.json();
      // merge updated object into local subscriptions state
      setSubscriptions((prev) => ({ ...(prev || {}), ...(updated || {}) }));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save subscription");
    }
  };

  const submitChangeWasher = async () => {
    if (!subscriptions?._id) return;
    if (!selectedChangeWasher) { alert("Select a washer"); return; }
    setChangeSubmitting(true);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const body = {
        washerId: selectedChangeWasher._id ?? selectedChangeWasher,
        price: Number(cwPrice) || 0,
        transactionId: cwTxnId || undefined,
      };
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/updateWasher/${subscriptions._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error("Failed to change washer");
      const updated = await res.json();
      setSubscriptions((prev) => ({ ...(prev || {}), ...(updated || {}) }));
      setChangeWasherOpen(false);
      // refresh list
      fetchSubscriptions();
    } catch (err) {
      console.error(err);
      alert("Failed to change washer");
    } finally {
      setChangeSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const formatValue = (v) => {
    if (v === null || v === undefined) return "-";
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return String(v);
    if (Array.isArray(v)) return v.map(formatValue).join(", ");
    if (typeof v === "object") {
      if ("name" in v) return v.name;
      return JSON.stringify(v);
    }
    return String(v);
  };

  function formatSchedule(schedule) {
    if (!schedule) return "-";

    const fmt = (s) => {
      if (!s) return "";
      if (typeof s === "string" || typeof s === "number") return String(s);
      if (typeof s === "object") {
        if (s.week || s.weekNo || s.weekNumber) {
          const wk = s.week ?? s.weekNo ?? s.weekNumber;
          const day = s.day || "";
          const time = s.time || "";
          return `Week ${wk}${day ? " " + day : ""}${time ? " " + time : ""}`.trim();
        }
        if (s.day || s.time) return `${s.day || ""}${s.day && s.time ? " " : ""}${s.time || ""}`.trim();
        return Object.values(s).join(" ");
      }
      return String(s);
    };

    if (typeof schedule === "object" && !Array.isArray(schedule)) {
      const parts = [];

      if (Array.isArray(schedule.exterior) && schedule.exterior.length > 0) {
        const ext = schedule.exterior.map(fmt).filter(Boolean).join("; ");
        if (ext) parts.push(`Exterior: ${ext}`);
      }

      // handle interior as either array or object containing weeklySchedule
      if (Array.isArray(schedule.interior) && schedule.interior.length > 0) {
        const inten = schedule.interior.map(fmt).filter(Boolean).join("; ");
        if (inten) parts.push(`Interior: ${inten}`);
      } else if (schedule.interior && Array.isArray(schedule.interior.weeklySchedule)) {
        const inten = schedule.interior.weeklySchedule.map(fmt).filter(Boolean).join("; ");
        if (inten) parts.push(`Interior: ${inten}`);
      }

      if (parts.length) return parts.join(" | ");

      // fallback: if object has days/time fields
      if (schedule.days || schedule.time || schedule.timeFrom || schedule.timeRange) {
        const days = Array.isArray(schedule.days) ? schedule.days.join(", ") : schedule.days || "";
        const timeLabel =
          schedule.timeRange && schedule.timeRange.from
            ? `${schedule.timeRange.from}${schedule.timeRange.to ? ` - ${schedule.timeRange.to}` : ""}`
            : schedule.timeFrom
            ? `${schedule.timeFrom}${schedule.timeTo ? ` - ${schedule.timeTo}` : ""}`
            : schedule.time || "";
        return [days, timeLabel].filter(Boolean).join(" ");
      }

      return JSON.stringify(schedule);
    }

    if (Array.isArray(schedule)) {
      return schedule.map(fmt).filter(Boolean).join(", ");
    }

    return String(schedule);
  }
  
  if (!carId && !userId) {
    return <Typography variant="body2">Provide carId or userId to load subscriptions.</Typography>;
  }

  if (loading) return <CircularProgress size={24} />;
  if (error) return <Typography color="error">{error}</Typography>;
  console.log(subscriptions);
  if (!subscriptions || Object.keys(subscriptions).length === 0) {
    return <Typography>No subscriptions found.</Typography>;
  }
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "50%", md: "33%" },
        flex: "0 0 auto",
        boxSizing: "border-box",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          boxSizing: "border-box",
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%", // ensures footer stays at bottom even with little content
        }}
      >
        <Typography variant="h6" gutterBottom>
          Subscription Details
        </Typography>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {!isEditing ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Typography variant="body2" sx={{ color: subscriptions.status === "cancelled" ? "red" : "green" }}>
                  {subscriptions.status === "active" ? "Active" : "Cancelled"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Washer</Typography>
                <Typography variant="body2" >
                  {subscriptions.washerId?.name ? subscriptions.washerId.name : "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Subscription Plan</Typography>
                <Typography variant="body2">{formatValue(subscriptions.currentSubscriptionPlan)}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Start Date</Typography>
                <Typography variant="body2">
                  {subscriptions.startDate ? new Date(subscriptions.startDate).toLocaleDateString() : "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Expire Date</Typography>
                <Typography variant="body2">
                  {subscriptions.expireDate ? new Date(subscriptions.expireDate).toLocaleDateString() : "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Price</Typography>
                <Typography variant="body2">{formatValue(subscriptions.currentSubscriptionPlanPrice)}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Wash type</Typography>
                <Typography variant="body2">
                  {subscriptions.currentWashType?['waterless', 'bucket', 'jet-spray', 'full-wash'].includes(subscriptions.currentWashType) && <img
                      src={subscriptions.currentWashType === 'waterless'?"/dusting.png" : subscriptions.currentWashType === 'bucket' ?"/bucket.png":  subscriptions.currentWashType === 'jet-spray'? 'jetSpray.png' : subscriptions.currentWashType === 'full-wash'? 'fullwash.png':""}
                      alt="Wash Type"
                      style={{
                        width: 30,
                        height: 30,
                        objectFit: 'contain',
                        borderRadius: 2,
                        marginRight: 2,
                      }}
                    />:'-'}
                  </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography variant="body2" color="textSecondary">Schedule</Typography>
                <Typography variant="body2" sx={{ textAlign: "right" }}>{formatSchedule(subscriptions.currentSchedule)}</Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Plan</Typography>
                <Autocomplete
                  size="small"
                  value={editPlan || ""}
                  onChange={(e, v) => setEditPlan(v?._id ?? v ?? "")}
                  options={PLAN_OPTIONS}
                  getOptionLabel={(opt) => opt?.name ?? opt ?? ""}
                  sx={{ minWidth: 140 }}
                  renderInput={(params) => <TextField {...params} placeholder="Select plan" />}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Wash Type</Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={editWashType || ""}
                    label="Wash Type"
                    onChange={(e) => setEditWashType(e.target.value)}
                  >
                    {WASH_TYPES.map(w => (
                      <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Price</Typography>
                <TextField size="small" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} sx={{ width: 120 }} />
              </Box>

              {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">Washer</Typography>
                <Autocomplete
                  size="small"
                  value={editWasher}
                  onChange={(e, v) => setEditWasher(v)}
                  onInputChange={(e, v) => fetchWasherOptions(v)}
                  options={washerOptions}
                  getOptionLabel={(opt) => opt?.name ?? opt ?? ""}
                  loading={washerLoading}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} placeholder="Select washer" />}
                />
              </Box> */}

              {/* schedule editor shown when editing */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Schedule</Typography>
                {getPackCount(editPlan).exterior > 0 && (
                  <>
                    {[...Array(getPackCount(editPlan).exterior)].map((_, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                        <TextField
                          select
                          label={`Day ${idx + 1}`}
                          value={schedule[idx]?.day || ''}
                          onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                          style={{ minWidth: 140 }}
                          size="small"
                        >
                          {WEEK_DAYS.map(day => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label={`Time ${idx + 1}`}
                          type="time"
                          value={schedule[idx]?.time || ''}
                          onChange={e => handleScheduleChange(idx, 'time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                        />
                      </div>
                    ))}
                  </>
                )}
              </Box>

              {/* interior scheduling UI (based on plan counts) */}
              {isEditing && (() => {
                const counts = getPackCount(editPlan);
                if (counts.interior === 0) return null;
                const selectedExteriorDays = schedule.slice(0, counts.exterior).map(s => s.day).filter(Boolean);
                if (selectedExteriorDays.length === 0) {
                  return (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Please select exterior wash days first to configure interior wash schedule.
                      </Typography>
                    </Box>
                  );
                }

                const labelForInterior = counts.interior === 4 ? 'I4 - Weekly' : counts.interior === 2 ? 'I2 - Bi-weekly' : 'I1 - Monthly';
                return (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Interior Wash Schedule</Typography>

                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Interior wash schedule ({labelForInterior}):
                      </Typography>
                      {(interiorSchedule.weeklySchedule || []).map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                          <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Week</InputLabel>
                            <Select
                              value={item.week}
                              label="Week"
                              onChange={(e) => handleWeeklyInteriorScheduleChange(idx, 'week', e.target.value)}
                            >
                              {WEEK_OPTIONS.slice(0, 4).map(week => (
                                <MenuItem key={week.value} value={week.value}>{week.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Day</InputLabel>
                            <Select
                              value={item.day}
                              label="Day"
                              onChange={(e) => handleWeeklyInteriorScheduleChange(idx, 'day', e.target.value)}
                            >
                              {selectedExteriorDays.map(day => (
                                <MenuItem key={day} value={day}>{day}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </>
          )}
        </Box>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start", gap: 1 }}>
          {!isEditing ? (
            <>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  setIsEditing(true);
                  fetchWasherOptions();
                }}
                >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                sx={{ color: "error.main" }}
                onClick={handleSkip}
                disabled={skipSubmitting}
              >
                {skipSubmitting ? "Skipping..." : "Skip"}
              </Button>
              <Button
                size="small"
                color="error"
                sx={{ color: "error.main" }}
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  setPauseDate(tomorrow.toISOString().slice(0, 10));
                  setPauseDialogOpen(true);
                }}
              >
                Pause
              </Button>
              <Button
                  size="small"
                  color="error"
                  sx={{ color: "error.main" }}
                  onClick={openChangeWasherDialog}
              >
                Change washer
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={handleEditCancel}>
                Cancel
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleEditSave}>
                Save
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Change washer dialog */}
      <Dialog open={changeWasherOpen} onClose={() => { if (!changeSubmitting) setChangeWasherOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Change Washer & Price</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Autocomplete
              size="small"
              value={selectedChangeWasher}
              onChange={(e, v) => setSelectedChangeWasher(v)}
              onInputChange={(e, v) => fetchWasherOptions(v)}
              options={washerOptions}
              getOptionLabel={(opt) => opt?.name ?? opt ?? ""}
              loading={washerLoading}
              renderInput={(params) => <TextField {...params} label="Washer" placeholder="Select washer" />}
            />

            <TextField
              label="Price"
              size="small"
              value={cwPrice}
              onChange={(e) => setCwPrice(e.target.value)}
            />

            <TextField
              label="Transaction ID"
              size="small"
              value={cwTxnId}
              onChange={(e) => setCwTxnId(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeWasherOpen(false)} disabled={changeSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={submitChangeWasher} disabled={changeSubmitting}>
            {changeSubmitting ? "Saving..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause subscription dialog */}
      <Dialog open={pauseDialogOpen} onClose={() => { if (!pauseSubmitting) setPauseDialogOpen(false); }}>
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select a future date until which the subscription should be paused.
          </Typography>
          <TextField
            label="Pause until"
            type="date"
            value={pauseDate}
            onChange={(e) => setPauseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ min: (() => { const d = new Date(); d.setDate(d.getDate()+1); return d.toISOString().slice(0,10); })() }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)} disabled={pauseSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!pauseDate) { alert("Select a date"); return; }
              const selected = new Date(pauseDate);
              const tomorrow = new Date(); tomorrow.setHours(0,0,0,0); tomorrow.setDate(tomorrow.getDate()+1);
              if (selected < tomorrow) { alert("Please choose a future date"); return; }
              if (!subscriptions?._id) { alert("No subscription selected"); return; }
              setPauseSubmitting(true);
              try {
                const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
                const res = await fetch(
                  `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/pause/${subscriptions._id}`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: token ? `Bearer ${token}` : "",
                    },
                    body: JSON.stringify({ pauseUntilDate: pauseDate }),
                  }
                );
                console.log(res)
                if (!res.ok) throw new Error("Failed to pause subscription");
                const updated = await res.json();
                setSubscriptions((prev) => ({ ...(prev || {}), ...(updated || {}) }));
                setPauseDialogOpen(false);
                setPauseDate("");
                fetchSubscriptions();
              } catch (err) {
                console.error(err);
                alert("Failed to pause subscription");
              } finally {
                setPauseSubmitting(false);
              }
            }}
            disabled={pauseSubmitting}
          >
            {pauseSubmitting ? "Pausing..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
